import { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { Input } from '../../../components/shared/StyledComponents'
import { api } from '../../../services/api'
import { Chip } from './linkedinStyles'

/**
 * Searchable pickers shared by the project forms: the repos / papers / posts a
 * project bundles, and the Redmine project it is linked to.
 */

export interface PickedItem {
    id: number
    label: string
}

export type ItemKind = 'repo' | 'paper' | 'post'

const KIND_TITLES: Record<ItemKind, string> = { repo: 'Repositorios', paper: 'Papers', post: 'Publicaciones' }

const PickerBox = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
`

const Results = styled.div`
    max-height: 170px;
    overflow-y: auto;
    border: 1px solid ${({ theme }) => theme.colors.border};
    border-radius: ${({ theme }) => theme.borderRadius.sm};
    background: ${({ theme }) => theme.colors.cardBg};
`

const ResultRow = styled.button`
    display: block;
    width: 100%;
    text-align: left;
    padding: 0.4rem 0.6rem;
    border: none;
    background: none;
    font: inherit;
    font-size: 0.85rem;
    cursor: pointer;

    &:hover:not(:disabled) {
        background: ${({ theme }) => theme.colors.secondary};
    }
    &:disabled {
        opacity: 0.5;
        cursor: default;
    }
`

const Picked = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem;
`

const Remove = styled.button`
    border: none;
    background: none;
    cursor: pointer;
    font: inherit;
    padding: 0 0 0 0.2rem;
`

const shorten = (text: string, n = 70) => (text.length > n ? `${text.slice(0, n - 1)}…` : text)

/** A short human label for a search hit of each kind. */
export const itemLabel = (kind: ItemKind, row: any): string => {
    if (kind === 'repo') return `${row.owner}/${row.name}`
    if (kind === 'paper') return shorten(row.title || row.external_id || row.url || `paper #${row.id}`)
    return shorten(`${row.author || 'Autor desconocido'} · ${row.summary || 'sin resumen'}`)
}

const search = async (kind: ItemKind, q: string): Promise<PickedItem[]> => {
    const filters = { search: q, limit: 8 }
    const res =
        kind === 'repo'
            ? await api.linkedin.getRepos(filters)
            : kind === 'paper'
              ? await api.linkedin.getPapers(filters)
              : await api.linkedin.getPosts(filters)
    return (res.data.items as any[]).map((row) => ({ id: row.id, label: itemLabel(kind, row) }))
}

interface ItemPickerProps {
    kind: ItemKind
    selected: PickedItem[]
    onChange: (items: PickedItem[]) => void
}

export const ItemPicker: React.FC<ItemPickerProps> = ({ kind, selected, onChange }) => {
    const [query, setQuery] = useState('')
    const [results, setResults] = useState<PickedItem[]>([])
    const [searching, setSearching] = useState(false)
    const seq = useRef(0)

    useEffect(() => {
        if (query.trim().length < 2) {
            setResults([])
            return
        }
        const mine = ++seq.current
        const handle = setTimeout(async () => {
            setSearching(true)
            try {
                const found = await search(kind, query.trim())
                if (mine === seq.current) setResults(found)
            } catch {
                if (mine === seq.current) setResults([])
            } finally {
                if (mine === seq.current) setSearching(false)
            }
        }, 300)
        return () => clearTimeout(handle)
    }, [query, kind])

    const taken = new Set(selected.map((s) => s.id))
    return (
        <PickerBox>
            <strong style={{ fontSize: '0.85rem' }}>
                {KIND_TITLES[kind]} ({selected.length})
            </strong>
            <Picked>
                {selected.map((item) => (
                    <Chip key={item.id} $bg="#e3f2fd" $color="#0d47a1">
                        {item.label}
                        <Remove
                            type="button"
                            aria-label={`Quitar ${item.label}`}
                            onClick={() => onChange(selected.filter((s) => s.id !== item.id))}
                        >
                            ×
                        </Remove>
                    </Chip>
                ))}
            </Picked>
            <Input
                value={query}
                placeholder={`Buscar ${KIND_TITLES[kind].toLowerCase()}…`}
                aria-label={`Buscar ${KIND_TITLES[kind].toLowerCase()}`}
                onChange={(e) => setQuery(e.target.value)}
            />
            {(results.length > 0 || searching) && (
                <Results>
                    {searching && <ResultRow disabled>Buscando…</ResultRow>}
                    {results.map((r) => (
                        <ResultRow
                            key={r.id}
                            type="button"
                            disabled={taken.has(r.id)}
                            onClick={() => {
                                onChange([...selected, r])
                                setQuery('')
                                setResults([])
                            }}
                        >
                            {r.label}
                            {taken.has(r.id) ? ' · ya añadido' : ''}
                        </ResultRow>
                    ))}
                </Results>
            )}
        </PickerBox>
    )
}

export interface PickedRedmine {
    id: number
    name: string
}

interface RedminePickerProps {
    value: PickedRedmine | null
    onChange: (value: PickedRedmine | null) => void
}

/** Search the Redmine catalog and pick one project (or none). */
export const RedmineProjectPicker: React.FC<RedminePickerProps> = ({ value, onChange }) => {
    const [query, setQuery] = useState('')
    const [results, setResults] = useState<PickedRedmine[]>([])
    const seq = useRef(0)

    useEffect(() => {
        if (query.trim().length < 2) {
            setResults([])
            return
        }
        const mine = ++seq.current
        const handle = setTimeout(async () => {
            try {
                const res = await api.redmine.searchProjects(query.trim(), 8)
                const rows: any[] = Array.isArray(res.data) ? res.data : res.data?.projects || res.data?.items || []
                if (mine === seq.current) setResults(rows.map((r) => ({ id: r.id, name: r.name })))
            } catch {
                if (mine === seq.current) setResults([])
            }
        }, 300)
        return () => clearTimeout(handle)
    }, [query])

    return (
        <PickerBox>
            <strong style={{ fontSize: '0.85rem' }}>Proyecto de Redmine</strong>
            {value ? (
                <Picked>
                    <Chip $bg="#e3f2fd" $color="#0d47a1">
                        🔗 {shorten(value.name, 90)}
                        <Remove type="button" aria-label="Quitar el proyecto de Redmine" onClick={() => onChange(null)}>
                            ×
                        </Remove>
                    </Chip>
                </Picked>
            ) : (
                <>
                    <Input
                        value={query}
                        placeholder="Buscar proyecto de Redmine…"
                        aria-label="Buscar proyecto de Redmine"
                        onChange={(e) => setQuery(e.target.value)}
                    />
                    {results.length > 0 && (
                        <Results>
                            {results.map((r) => (
                                <ResultRow
                                    key={r.id}
                                    type="button"
                                    onClick={() => {
                                        onChange(r)
                                        setQuery('')
                                        setResults([])
                                    }}
                                >
                                    {r.name}
                                </ResultRow>
                            ))}
                        </Results>
                    )}
                </>
            )}
        </PickerBox>
    )
}
